import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuthStore } from "@/stores/auth/auth-store";
import { SEO } from "@/components/seo/SEO";
import { TIER_DISPLAY } from "@/types/billing";
import {
  normalizePendingRegistrationEmail,
  readPendingRegistrationEmail,
  savePendingRegistrationEmail,
} from "@/utils/pendingRegistrationEmail";
import {
  appendPendingRegistrationSelectedPlanParams,
  clearPendingRegistrationSelectedPlan,
  getPendingRegistrationSelectedPlanPath,
  isBillingInterval,
  isRegistrationTier,
  parsePendingRegistrationSelectedPlan,
  savePendingRegistrationSelectedPlan,
} from "@/utils/pendingRegistrationSelectedPlan";
import {
  clearPendingRegistrationAccountKind,
  savePendingRegistrationAccountKind,
  SPONSORSHIP_ACCOUNT_KIND,
} from "@/utils/pendingRegistrationAccountContext";
import {
  getPublicAuthChallengeFailureMessage,
  isPublicAuthChallengeFailure,
} from "@/services/api/publicAuthChallenge";
import {
  api,
  getApiErrorMessage,
  getApiErrorStatus,
  isApiError,
} from "@/services/api/client";

const MIN_PASSWORD_LENGTH = 8;
const MIN_USERNAME_LENGTH = 3;
const MAX_USERNAME_LENGTH = 30;
const MAX_NAME_LENGTH = 50;
const USERNAME_PATTERN = /^[a-zA-Z0-9_-]+$/;
const PASSWORD_GUIDANCE =
  "Use at least 8 characters. Longer is better, and avoid reusing an old password.";
const USERNAME_GUIDANCE =
  "Use 3-30 characters: letters, numbers, underscores, and hyphens only.";
const LEGAL_ACCEPTANCE_ERROR =
  "You must accept the Terms of Service and Privacy Policy to register.";
const SELF_REGISTRATION_DISABLED_API_MESSAGE =
  "Registration is currently disabled. Please contact support.";
const SELF_REGISTRATION_DISABLED_MESSAGE =
  "Self-Registration Currently Disabled. Use an invitation link or contact support to create an account.";
const SPONSORSHIP_REGISTRATION_CONFLICT_MESSAGE =
  "An account with this email already exists. Sign in with that account to continue accepting this invitation.";
const INVITATION_ACCEPT_PATH_PREFIX = "/invitations/accept/";
const INVITATION_REDIRECT_KEY = "invitation_redirect";
const INVITATION_REDIRECT_EMAIL_KEY = "invitation_redirect_email";
const REGISTRATION_DRAFT_KEY = "terramatrix.registration.formDraft";
const SPONSORSHIP_INVITE_TOKEN_PATTERN = /^[a-f0-9]{64}$/i;

interface RegistrationFormData {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
  legalAcceptance: boolean;
}

interface RegistrationFormDraft {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  legalAcceptance: boolean;
}

const RegistrationDraftSchema = z.object({
  firstName: z.string().max(MAX_NAME_LENGTH).optional().default(""),
  lastName: z.string().max(MAX_NAME_LENGTH).optional().default(""),
  email: z.string().optional().default(""),
  username: z.string().max(MAX_USERNAME_LENGTH).optional().default(""),
  legalAcceptance: z.boolean().optional().default(false),
});
const SponsorshipInvitePreviewSchema = z.object({
  invitedAccountExists: z.boolean().optional(),
});

const getSafeStoredInvitationRedirect = (
  storedRedirect: string | null,
): string | null => {
  if (
    storedRedirect === null ||
    !storedRedirect.startsWith("/") ||
    storedRedirect.startsWith("//") ||
    storedRedirect.includes("\\")
  ) {
    return null;
  }

  try {
    const parsedRedirect = new URL(storedRedirect, window.location.origin);
    if (
      parsedRedirect.origin !== window.location.origin ||
      !parsedRedirect.pathname.startsWith(INVITATION_ACCEPT_PATH_PREFIX)
    ) {
      return null;
    }

    return `${parsedRedirect.pathname}${parsedRedirect.search}${parsedRedirect.hash}`;
  } catch {
    return null;
  }
};

const emptyRegistrationFormData = (
  email = "",
): RegistrationFormData => ({
  firstName: "",
  lastName: "",
  email,
  username: "",
  password: "",
  confirmPassword: "",
  legalAcceptance: false,
});

const readRegistrationDraft = (): RegistrationFormDraft | null => {
  try {
    const rawDraft = window.sessionStorage.getItem(REGISTRATION_DRAFT_KEY);
    if (!rawDraft) {
      return null;
    }

    const parsedDraft: unknown = JSON.parse(rawDraft);
    const draftResult = RegistrationDraftSchema.safeParse(parsedDraft);
    if (!draftResult.success) {
      return null;
    }

    const draft = draftResult.data;
    return {
      firstName: draft.firstName,
      lastName: draft.lastName,
      email: normalizePendingRegistrationEmail(draft.email),
      username: draft.username,
      legalAcceptance: draft.legalAcceptance,
    };
  } catch {
    return null;
  }
};

const writeRegistrationDraft = (draft: RegistrationFormDraft): void => {
  try {
    window.sessionStorage.setItem(
      REGISTRATION_DRAFT_KEY,
      JSON.stringify(draft),
    );
  } catch {
    return;
  }
};

const clearRegistrationDraft = (): void => {
  try {
    window.sessionStorage.removeItem(REGISTRATION_DRAFT_KEY);
  } catch {
    return;
  }
};

const hasMalformedSelectedPlanLink = (
  selectedTier: string | null,
  billingInterval: string | null,
): boolean =>
  isRegistrationTier(selectedTier) &&
  selectedTier !== "free" &&
  !isBillingInterval(billingInterval);

const getSponsorshipInviteToken = (value: string | null): string => {
  const trimmedValue = value?.trim() ?? "";
  return SPONSORSHIP_INVITE_TOKEN_PATTERN.test(trimmedValue)
    ? trimmedValue
    : "";
};

const maskEmailForInviteDebug = (email: string): string => {
  const normalizedEmail = normalizePendingRegistrationEmail(email);
  const [localPart = "", domain = ""] = normalizedEmail.split("@");
  if (!localPart || !domain) {
    return normalizedEmail ? "[invalid-email]" : "[empty]";
  }

  return `${localPart.slice(0, 2)}***@${domain}`;
};

const logSponsorshipInviteRegisterDebug = (
  event: string,
  metadata: Record<string, unknown>,
): void => {
  console.info(`[sponsorship-invite:register] ${event}`, metadata);
};

const buildSponsorshipInvitationRedirect = (token: string): string =>
  `/invitations/accept/${encodeURIComponent(token)}?kind=sponsorship`;

const storeInvitationRedirectValue = (key: string, value: string): void => {
  window.sessionStorage.setItem(key, value);
};

const removeInvitationRedirectValue = (key: string): void => {
  window.sessionStorage.removeItem(key);
};

const getStoredInvitationRedirectValue = (key: string): string | null =>
  window.sessionStorage.getItem(key);

const saveSponsorshipInvitationRedirect = (
  token: string,
  invitedEmail?: string,
): void => {
  try {
    storeInvitationRedirectValue(
      INVITATION_REDIRECT_KEY,
      buildSponsorshipInvitationRedirect(token),
    );
    const normalizedEmail = normalizePendingRegistrationEmail(
      invitedEmail ?? "",
    );
    if (normalizedEmail) {
      storeInvitationRedirectValue(
        INVITATION_REDIRECT_EMAIL_KEY,
        normalizedEmail,
      );
    } else {
      removeInvitationRedirectValue(INVITATION_REDIRECT_EMAIL_KEY);
    }
    logSponsorshipInviteRegisterDebug("stored redirect", {
      hasToken: Boolean(token),
      invitedEmail: maskEmailForInviteDebug(normalizedEmail),
      hasInvitedEmail: Boolean(normalizedEmail),
    });
  } catch {
    logSponsorshipInviteRegisterDebug("failed to store redirect", {
      hasToken: Boolean(token),
    });
    return;
  }
};

const withLoginInviteParams = (
  path: string,
  email: string,
  sponsorshipInviteToken?: string,
): string => {
  const normalizedEmail = normalizePendingRegistrationEmail(email);
  if (!normalizedEmail && !sponsorshipInviteToken) {
    return path;
  }

  const [pathname, query = ""] = path.split("?");
  const params = new URLSearchParams(query);
  if (normalizedEmail) {
    params.set("email", normalizedEmail);
  }
  if (sponsorshipInviteToken) {
    params.set("sponsorshipInviteToken", sponsorshipInviteToken);
  }
  const queryString = params.toString();

  return queryString ? `${pathname}?${queryString}` : pathname;
};

const getRegistrationSubmissionErrorMessage = (
  err: unknown,
  options?: { isSponsorshipRegistration?: boolean },
): string => {
  if (isPublicAuthChallengeFailure(err)) {
    return getPublicAuthChallengeFailureMessage();
  }

  const apiMessage = getApiErrorMessage(err, "");
  const apiStatus = getApiErrorStatus(err);
  if (
    options?.isSponsorshipRegistration &&
    apiStatus === 409 &&
    (!apiMessage || apiMessage === "Registration failed")
  ) {
    return SPONSORSHIP_REGISTRATION_CONFLICT_MESSAGE;
  }

  if (apiMessage) {
    return apiMessage === SELF_REGISTRATION_DISABLED_API_MESSAGE
      ? SELF_REGISTRATION_DISABLED_MESSAGE
      : apiMessage;
  }

  if (err instanceof Error) {
    return err.message === SELF_REGISTRATION_DISABLED_API_MESSAGE
      ? SELF_REGISTRATION_DISABLED_MESSAGE
      : err.message;
  }

  return "Registration failed";
};

const getPlanSummary = (
  selectedPlan: ReturnType<typeof parsePendingRegistrationSelectedPlan>,
) => {
  if (!selectedPlan) {
    return "";
  }

  const display = TIER_DISPLAY[selectedPlan.tier];

  if (selectedPlan.tier === "free") {
    return `${display.name} plan`;
  }

  const price =
    selectedPlan.billingInterval === "annual"
      ? `$${display.annualPrice}/year`
      : `$${display.monthlyPrice}/month`;

  return `${display.name} plan, ${price}`;
};

const getPasswordValidationError = (password: string): string => {
  if (password.length === 0) {
    return "Password is required.";
  }

  if (password.length < MIN_PASSWORD_LENGTH) {
    return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
  }

  return "";
};

const getRequiredNameValidationError = (
  value: string,
  label: "First name" | "Last name",
): string => {
  const trimmedValue = value.trim();
  if (trimmedValue.length === 0) {
    return `${label} is required.`;
  }

  if (trimmedValue.length > MAX_NAME_LENGTH) {
    return `${label} cannot exceed ${MAX_NAME_LENGTH} characters.`;
  }

  return "";
};

const getUsernameValidationError = (username: string): string => {
  if (username.trim().length === 0) {
    return "Username is required.";
  }

  if (
    username.length < MIN_USERNAME_LENGTH ||
    username.length > MAX_USERNAME_LENGTH
  ) {
    return `Username must be ${MIN_USERNAME_LENGTH}-${MAX_USERNAME_LENGTH} characters.`;
  }

  if (!USERNAME_PATTERN.test(username)) {
    return "Username can only contain letters, numbers, underscores, and hyphens.";
  }

  return "";
};

const getConfirmPasswordValidationError = (
  password: string,
  confirmPassword: string,
): string => {
  if (confirmPassword.length === 0) {
    return "Please confirm your password.";
  }

  if (password !== confirmPassword) {
    return "Passwords do not match.";
  }

  return "";
};

export const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sponsorshipInviteToken = getSponsorshipInviteToken(
    searchParams.get("sponsorshipInviteToken"),
  );
  const queryEmail = normalizePendingRegistrationEmail(
    searchParams.get("email") ?? "",
  );
  const [formData, setFormData] = useState<RegistrationFormData>(() => {
    const draft = readRegistrationDraft();
    const email = queryEmail || draft?.email || readPendingRegistrationEmail();
    return {
      ...emptyRegistrationFormData(email),
      ...(draft
        ? {
            firstName: draft.firstName,
            lastName: draft.lastName,
            username: draft.username,
            legalAcceptance: draft.legalAcceptance,
          }
        : {}),
    };
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const { register } = useAuthStore();
  const selectedPlan = parsePendingRegistrationSelectedPlan(
    searchParams.get("selectedTier"),
    searchParams.get("billingInterval"),
  );
  const malformedSelectedPlanLink = hasMalformedSelectedPlanLink(
    searchParams.get("selectedTier"),
    searchParams.get("billingInterval"),
  );
  const loginPath = getPendingRegistrationSelectedPlanPath(
    "/login",
    selectedPlan,
  );
  const loginPathWithInvite = withLoginInviteParams(
    loginPath,
    formData.email,
    sponsorshipInviteToken,
  );
  type TextFormField = Exclude<keyof typeof formData, "legalAcceptance">;

  useEffect(() => {
    if (sponsorshipInviteToken) {
      logSponsorshipInviteRegisterDebug("landed on registration link", {
        hasToken: true,
        queryEmail: maskEmailForInviteDebug(queryEmail),
        hasQueryEmail: Boolean(queryEmail),
      });
      clearPendingRegistrationSelectedPlan();
      savePendingRegistrationAccountKind(SPONSORSHIP_ACCOUNT_KIND);
      saveSponsorshipInvitationRedirect(sponsorshipInviteToken, queryEmail);
      return;
    }

    clearPendingRegistrationAccountKind();
    if (selectedPlan) {
      savePendingRegistrationSelectedPlan(selectedPlan);
    }
  }, [queryEmail, selectedPlan, sponsorshipInviteToken]);

  useEffect(() => {
    if (!sponsorshipInviteToken) {
      return;
    }

    let isMounted = true;

    logSponsorshipInviteRegisterDebug("preview request started", {
      hasToken: true,
    });

    api
      .get<unknown>(`/sponsorships/token/${sponsorshipInviteToken}/preview`)
      .then((response) => {
        const preview = SponsorshipInvitePreviewSchema.safeParse(response.data);
        logSponsorshipInviteRegisterDebug("preview response received", {
          hasToken: true,
          validShape: preview.success,
          invitedAccountExists: preview.success
            ? preview.data.invitedAccountExists
            : undefined,
          isMounted,
        });
        if (!isMounted || !preview.success) {
          return;
        }

        if (preview.data.invitedAccountExists === true) {
          logSponsorshipInviteRegisterDebug(
            "preview says invited account exists; staying on registration",
            {
              hasToken: true,
              email: maskEmailForInviteDebug(formData.email),
              signInLinkCarriesToken: loginPathWithInvite.includes(
                "sponsorshipInviteToken=",
              ),
            },
          );
        }
      })
      .catch((previewError: unknown) => {
        logSponsorshipInviteRegisterDebug("preview request failed", {
          hasToken: true,
          status: getApiErrorStatus(previewError),
          hasMessage: Boolean(getApiErrorMessage(previewError, "")),
        });
        return;
      });

    return () => {
      isMounted = false;
    };
  }, [loginPathWithInvite, navigate, sponsorshipInviteToken]);

  useEffect(() => {
    writeRegistrationDraft({
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      username: formData.username,
      legalAcceptance: formData.legalAcceptance,
    });
  }, [
    formData.email,
    formData.firstName,
    formData.lastName,
    formData.legalAcceptance,
    formData.username,
  ]);

  const handleChange =
    (field: TextFormField) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (error) {
        setError("");
      }
      setFormData((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));
    };

  const handleLegalAcceptanceChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (error === LEGAL_ACCEPTANCE_ERROR) {
      setError("");
    }

    setFormData((prev) => ({
      ...prev,
      legalAcceptance: e.target.checked,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);

    if (malformedSelectedPlanLink) {
      setError("Choose a valid paid plan before creating an account.");
      setSuccess("");
      return;
    }

    const firstNameValidationError = getRequiredNameValidationError(
      formData.firstName,
      "First name",
    );
    if (firstNameValidationError) {
      setError(firstNameValidationError);
      setSuccess("");
      return;
    }

    const lastNameValidationError = getRequiredNameValidationError(
      formData.lastName,
      "Last name",
    );
    if (lastNameValidationError) {
      setError(lastNameValidationError);
      setSuccess("");
      return;
    }

    const usernameValidationError = getUsernameValidationError(
      formData.username,
    );
    if (usernameValidationError) {
      setError(usernameValidationError);
      setSuccess("");
      return;
    }

    const passwordValidationError = getPasswordValidationError(
      formData.password,
    );
    if (passwordValidationError) {
      setError(passwordValidationError);
      setSuccess("");
      return;
    }

    const confirmPasswordValidationError = getConfirmPasswordValidationError(
      formData.password,
      formData.confirmPassword,
    );
    if (confirmPasswordValidationError) {
      setError(confirmPasswordValidationError);
      setSuccess("");
      return;
    }

    if (!formData.legalAcceptance) {
      setError(LEGAL_ACCEPTANCE_ERROR);
      setSuccess("");
      return;
    }

    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const trimmedEmail = formData.email.trim();
      const trimmedFirstName = formData.firstName.trim();
      const trimmedLastName = formData.lastName.trim();
      if (sponsorshipInviteToken) {
        logSponsorshipInviteRegisterDebug("registration submit", {
          hasToken: true,
          email: maskEmailForInviteDebug(trimmedEmail),
          hasTokenInPayload: true,
        });
      }
      const result = await register({
        email: trimmedEmail,
        password: formData.password,
        username: formData.username,
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        legalAcceptance: {
          accepted: true,
        },
        ...(selectedPlan ? { selectedPlan } : {}),
        ...(sponsorshipInviteToken ? { sponsorshipInviteToken } : {}),
      });

      if (result.status === "pending_verification") {
        clearRegistrationDraft();
        savePendingRegistrationEmail(result.email);
        const pendingSelectedPlan = sponsorshipInviteToken
          ? undefined
          : result.selectedPlan ?? selectedPlan;
        if (sponsorshipInviteToken) {
          logSponsorshipInviteRegisterDebug("registration pending verification", {
            hasToken: true,
            email: maskEmailForInviteDebug(result.email),
          });
          clearPendingRegistrationSelectedPlan();
          savePendingRegistrationAccountKind(SPONSORSHIP_ACCOUNT_KIND);
          navigate("/check-email?accountKind=sponsorship", {
            replace: true,
          });
          return;
        }

        clearPendingRegistrationAccountKind();
        if (pendingSelectedPlan) {
          savePendingRegistrationSelectedPlan(pendingSelectedPlan);
        }
        const params = new URLSearchParams();
        appendPendingRegistrationSelectedPlanParams(params, pendingSelectedPlan);
        const query = params.toString();
        navigate(query ? `/check-email?${query}` : "/check-email", {
          replace: true,
        });
        return;
      }

      if (sponsorshipInviteToken) {
        logSponsorshipInviteRegisterDebug("registration succeeded", {
          hasToken: true,
          email: maskEmailForInviteDebug(result.user.email),
          autoAccepted: Boolean(result.sponsorship),
        });
      }
      setSuccess("Account created successfully! Redirecting...");
      clearRegistrationDraft();
      // Sponsorship-invite flow: the backend already accepted the invite and
      // provisioned the user. Go straight to the provisioned home and clear
      // the sessionStorage handoff we no longer need.
      if (result.sponsorship) {
        removeInvitationRedirectValue(INVITATION_REDIRECT_KEY);
        removeInvitationRedirectValue(INVITATION_REDIRECT_EMAIL_KEY);
        clearPendingRegistrationAccountKind();
        const sponsorshipRedirect = result.sponsorship.redirectUrl;
        setTimeout(() => {
          navigate(sponsorshipRedirect);
        }, 1000);
        return;
      }
      setTimeout(() => {
        const storedInvitationRedirect = getStoredInvitationRedirectValue(
          INVITATION_REDIRECT_KEY,
        );
        const invitationRedirect = getSafeStoredInvitationRedirect(
          storedInvitationRedirect,
        );
        if (storedInvitationRedirect !== null) {
          removeInvitationRedirectValue(INVITATION_REDIRECT_KEY);
          removeInvitationRedirectValue(INVITATION_REDIRECT_EMAIL_KEY);
        }
        if (invitationRedirect) {
          if (sponsorshipInviteToken) {
            logSponsorshipInviteRegisterDebug("navigating to stored invite", {
              hasToken: true,
            });
          }
          navigate(invitationRedirect);
          return;
        }
        if (sponsorshipInviteToken) {
          logSponsorshipInviteRegisterDebug(
            "no stored invite after registration success",
            {
              hasToken: true,
              hadStoredRedirect: storedInvitationRedirect !== null,
            },
          );
        }
        navigate("/dashboard");
      }, 2000);
    } catch (err) {
      if (sponsorshipInviteToken) {
        logSponsorshipInviteRegisterDebug("registration failed", {
          hasToken: true,
          email: maskEmailForInviteDebug(formData.email),
          status: getApiErrorStatus(err),
          code: isApiError(err) ? err.code : undefined,
          message: getApiErrorMessage(err, ""),
        });
      }
      setError(
        getRegistrationSubmissionErrorMessage(err, {
          isSponsorshipRegistration: Boolean(sponsorshipInviteToken),
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid =
    formData.firstName.trim() !== "" &&
    formData.lastName.trim() !== "" &&
    formData.email.trim() !== "" &&
    formData.username.trim() !== "" &&
    formData.password.length > 0 &&
    formData.confirmPassword.length > 0;
  const passwordValidationError =
    formData.password.length > 0
      ? getPasswordValidationError(formData.password)
      : "";
  const firstNameValidationError =
    formData.firstName.length > 0
      ? getRequiredNameValidationError(formData.firstName, "First name")
      : "";
  const lastNameValidationError =
    formData.lastName.length > 0
      ? getRequiredNameValidationError(formData.lastName, "Last name")
      : "";
  const usernameValidationError =
    formData.username.length > 0
      ? getUsernameValidationError(formData.username)
      : "";
  const confirmPasswordValidationError =
    formData.confirmPassword.length > 0
      ? getConfirmPasswordValidationError(
          formData.password,
          formData.confirmPassword,
        )
      : "";
  const legalAcceptanceValidationError =
    hasAttemptedSubmit && !formData.legalAcceptance
      ? LEGAL_ACCEPTANCE_ERROR
      : "";
  const canSubmit =
    isFormValid &&
    !malformedSelectedPlanLink &&
    firstNameValidationError === "" &&
    lastNameValidationError === "" &&
    usernameValidationError === "" &&
    passwordValidationError === "" &&
    confirmPasswordValidationError === "";

  return (
    <>
      <SEO
        title="Create Your TerraMatrix Account"
        description="Sign up for TerraMatrix to start building IoT monitoring systems with our visual editor platform. Join teams using AI-powered automation for agriculture, brewing, and restaurant operations."
        canonical="/register"
        noindex={true}
      />
      <Card>
        <CardHeader>
          <CardTitle>Create Account</CardTitle>
          <CardDescription>
            Join TerraMatrix and start building amazing components
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {selectedPlan && (
              <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-3 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium">Selected plan</span>
                  {selectedPlan.tier !== "free" && (
                    <Badge variant="secondary">
                      {selectedPlan.billingInterval === "annual"
                        ? "Annual"
                        : "Monthly"}
                    </Badge>
                  )}
                </div>
                <p className="mt-1 text-muted-foreground">
                  {getPlanSummary(selectedPlan)}
                </p>
              </div>
            )}
            {malformedSelectedPlanLink && (
              <div
                role="status"
                aria-live="polite"
                className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-200"
              >
                This selected plan link is missing a valid billing interval.
                Choose a plan again before creating an account.
              </div>
            )}
            {error && (
              <div
                role="alert"
                aria-live="assertive"
                className="bg-destructive/10 border border-destructive/20 text-destructive px-3 py-2 rounded-md text-sm"
              >
                {error}
              </div>
            )}
            {success && (
              <div
                role="status"
                aria-live="polite"
                className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 dark:text-emerald-400 px-3 py-2 rounded-md text-sm"
              >
                {success}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label
                  htmlFor="register-first-name"
                  className="text-sm font-medium"
                >
                  First Name
                </label>
                <Input
                  id="register-first-name"
                  placeholder="John"
                  value={formData.firstName}
                  onChange={handleChange("firstName")}
                  maxLength={MAX_NAME_LENGTH}
                  required
                  disabled={isLoading}
                />
                {firstNameValidationError && (
                  <p className="text-sm text-destructive">
                    {firstNameValidationError}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="register-last-name"
                  className="text-sm font-medium"
                >
                  Last Name
                </label>
                <Input
                  id="register-last-name"
                  placeholder="Doe"
                  value={formData.lastName}
                  onChange={handleChange("lastName")}
                  maxLength={MAX_NAME_LENGTH}
                  required
                  disabled={isLoading}
                />
                {lastNameValidationError && (
                  <p className="text-sm text-destructive">
                    {lastNameValidationError}
                  </p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <label htmlFor="register-email" className="text-sm font-medium">
                Email
              </label>
              <Input
                id="register-email"
                type="email"
                placeholder="john.doe@example.com"
                value={formData.email}
                onChange={handleChange("email")}
                required
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <label
                htmlFor="register-username"
                className="text-sm font-medium"
              >
                Username
              </label>
              <Input
                id="register-username"
                placeholder="johndoe"
                value={formData.username}
                onChange={handleChange("username")}
                minLength={MIN_USERNAME_LENGTH}
                maxLength={MAX_USERNAME_LENGTH}
                pattern="(?:[A-Za-z0-9_]|-)+"
                required
                disabled={isLoading}
              />
              <p className="text-sm text-muted-foreground">
                {USERNAME_GUIDANCE}
              </p>
              {usernameValidationError && (
                <p className="text-sm text-destructive">
                  {usernameValidationError}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <label
                htmlFor="register-password"
                className="text-sm font-medium"
              >
                Password
              </label>
              <Input
                id="register-password"
                type="password"
                placeholder="Create a strong password"
                value={formData.password}
                onChange={handleChange("password")}
                required
                disabled={isLoading}
              />
              <p className="text-sm text-muted-foreground">
                {PASSWORD_GUIDANCE}
              </p>
              {passwordValidationError && (
                <p className="text-sm text-destructive">
                  {passwordValidationError}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <label
                htmlFor="register-confirm-password"
                className="text-sm font-medium"
              >
                Confirm Password
              </label>
              <Input
                id="register-confirm-password"
                type="password"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={handleChange("confirmPassword")}
                required
                disabled={isLoading}
              />
              {confirmPasswordValidationError && (
                <p className="text-sm text-destructive">
                  {confirmPasswordValidationError}
                </p>
              )}
            </div>
            <div className="space-y-2 rounded-md border border-border/70 px-3 py-3">
              <div className="flex items-start gap-3">
                <input
                  id="register-legal-acceptance"
                  type="checkbox"
                  checked={formData.legalAcceptance}
                  onChange={handleLegalAcceptanceChange}
                  disabled={isLoading}
                  aria-describedby="register-legal-acceptance-copy register-legal-acceptance-links"
                  aria-labelledby="register-legal-acceptance-label"
                  className="mt-1 h-4 w-4 rounded border border-input text-primary focus:ring-2 focus:ring-primary/30"
                />
                <div className="space-y-1">
                  <label
                    id="register-legal-acceptance-label"
                    htmlFor="register-legal-acceptance"
                    className="text-sm font-medium leading-6"
                  >
                    I agree to the Terms of Service and Privacy Policy.
                  </label>
                  <p
                    id="register-legal-acceptance-copy"
                    className="text-sm text-muted-foreground"
                  >
                    Required before we can create your account.
                  </p>
                  <p
                    id="register-legal-acceptance-links"
                    className="text-sm text-muted-foreground"
                  >
                    Review the{" "}
                    <Link
                      to="/terms"
                      className="text-primary underline underline-offset-2"
                    >
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link
                      to="/privacy"
                      className="text-primary underline underline-offset-2"
                    >
                      Privacy Policy
                    </Link>{" "}
                    before creating your account.
                  </p>
                </div>
              </div>
              {legalAcceptanceValidationError && (
                <p className="text-sm text-destructive">
                  {legalAcceptanceValidationError}
                </p>
              )}
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || !canSubmit}
            >
              {isLoading ? "Creating Account..." : "Create Account"}
            </Button>
            <div className="text-center text-sm">
              Already have an account?{" "}
              <Link
                to={loginPathWithInvite}
                className="text-primary hover:underline"
              >
                Sign in
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </>
  );
};
